import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Users, Loader2, Download } from 'lucide-react'
import {
  getAdminAnalytics,
  getAnalyticsTrends,
  type AdminAnalytics,
  type TrendPoint,
} from '../../../lib/api'
import { CHART_PALETTE } from '../../../lib/chart-colors'
import StatCard from '../../../components/analytics/StatCard'
import TrendChart from '../../../components/analytics/TrendChart'
import PeriodSelector from '../../../components/analytics/PeriodSelector'
import { AnalyticsNav } from './AnalyticsOverview'
import { useAutoRefresh } from '../../../hooks/useAutoRefresh'
import { arrayToCSV, downloadCSV } from '../../../lib/csv-export'

function computeTrend(
  currentValue: number,
  trends: TrendPoint[],
  key: keyof TrendPoint
): { trend: 'up' | 'down' | 'flat'; trendValue: string } {
  if (trends.length === 0) {
    return { trend: 'flat', trendValue: '' }
  }

  const values = trends.map((t) => Number(t[key]))
  const avg = values.reduce((sum, v) => sum + v, 0) / values.length

  if (avg === 0 && currentValue === 0) {
    return { trend: 'flat', trendValue: '' }
  }
  if (avg === 0) {
    return { trend: 'up', trendValue: '+100%' }
  }

  const pctDiff = ((currentValue - avg) / avg) * 100
  const rounded = Math.round(Math.abs(pctDiff))

  if (rounded < 1) {
    return { trend: 'flat', trendValue: '' }
  }
  if (pctDiff > 0) {
    return { trend: 'up', trendValue: `+${rounded}%` }
  }
  return { trend: 'down', trendValue: `-${rounded}%` }
}

export default function UserAnalytics() {
  const navigate = useNavigate()
  const [period, setPeriod] = useState<string>('30d')
  const [analytics, setAnalytics] = useState<AdminAnalytics | null>(null)
  const [trends, setTrends] = useState<TrendPoint[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadData = useCallback(async () => {
    try {
      const [analyticsData, trendsData] = await Promise.all([
        getAdminAnalytics(period),
        getAnalyticsTrends(period),
      ])
      setAnalytics(analyticsData)
      setTrends(trendsData)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load user analytics')
      throw err // Allow useAutoRefresh to detect the error for backoff
    } finally {
      setLoading(false)
    }
  }, [period])

  useEffect(() => {
    setLoading(true)
    loadData()
  }, [loadData])

  useAutoRefresh(loadData, {
    baseInterval: 60000,
    maxInterval: 300000,
  })

  function handleExportCSV() {
    if (!analytics || analytics.users.length === 0) return
    const csv = arrayToCSV(analytics.users, [
      { key: 'email', label: 'Email' },
      { key: 'name', label: 'Name' },
      { key: 'app_count', label: 'Apps Created' },
    ])
    downloadCSV(csv, `analytics-users-${period}.csv`)
  }

  const dauTrend = analytics ? computeTrend(analytics.platform.active_users_daily, trends, 'active_users') : null
  const wauTrend = analytics ? computeTrend(analytics.platform.active_users_weekly, trends, 'active_users') : null
  const mauTrend = analytics ? computeTrend(analytics.platform.active_users_monthly, trends, 'active_users') : null

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold mb-2">User Analytics</h1>
          <p className="text-muted-foreground">User growth trends and active user breakdowns</p>
        </div>
        <div className="flex items-center gap-3">
          <PeriodSelector value={period} onChange={setPeriod} />
          <button
            onClick={handleExportCSV}
            disabled={!analytics || analytics.users.length === 0}
            className="flex items-center gap-2 px-3 py-2 border border-border rounded-lg hover:bg-secondary transition-colors text-sm disabled:opacity-50"
            title="Download data as CSV"
          >
            <Download className="w-4 h-4" />
            CSV
          </button>
        </div>
      </div>

      {/* Sub-navigation */}
      <AnalyticsNav />

      {/* Error banner */}
      {error && (
        <div className="mb-6 p-4 rounded-lg bg-destructive/10 text-destructive border border-destructive/20">
          {error}
        </div>
      )}

      {/* Loading state */}
      {loading && !analytics ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : !analytics ? (
        <div className="text-center py-12 text-muted-foreground">
          No analytics data available
        </div>
      ) : (
        <>
          {/* Active user cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <StatCard
              label="Daily Active Users"
              value={analytics.platform.active_users_daily}
              icon={Users}
              trend={dauTrend?.trend}
              trendValue={dauTrend?.trendValue}
            />
            <StatCard
              label="Weekly Active Users"
              value={analytics.platform.active_users_weekly}
              icon={Users}
              trend={wauTrend?.trend}
              trendValue={wauTrend?.trendValue}
            />
            <StatCard
              label="Monthly Active Users"
              value={analytics.platform.active_users_monthly}
              icon={Users}
              trend={mauTrend?.trend}
              trendValue={mauTrend?.trendValue}
            />
          </div>

          {/* User growth chart */}
          <div className="rounded-xl border border-border bg-card p-6 mb-8">
            <h3 className="text-lg font-semibold mb-4">User Growth</h3>
            <TrendChart
              data={trends}
              lines={[
                { dataKey: 'new_users', label: 'New Signups', color: CHART_PALETTE[0] },
                { dataKey: 'active_users', label: 'Active Users', color: CHART_PALETTE[1] },
              ]}
            />
          </div>

          {/* Top users table */}
          <div className="rounded-xl border border-border bg-card p-6">
            <h3 className="text-lg font-semibold mb-4">Top Users by Apps Created</h3>
            {analytics.users.length === 0 ? (
              <p className="text-muted-foreground text-sm">No user data available</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground">Email</th>
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground">Name</th>
                      <th className="text-right py-3 px-4 font-medium text-muted-foreground">Apps Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analytics.users.slice(0, 20).map((user) => (
                      <tr
                        key={user.user_id}
                        className="border-b border-border last:border-0 hover:bg-secondary/30 transition-colors cursor-pointer"
                        onClick={() => navigate(`/admin/users/${user.user_id}`)}
                      >
                        <td className="py-3 px-4">{user.email}</td>
                        <td className="py-3 px-4 text-muted-foreground">{user.name || '-'}</td>
                        <td className="py-3 px-4 text-right font-medium">{user.app_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}
